import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "login-screen" }
const _hoisted_2 = { class: "login-card" }
const _hoisted_3 = { class: "signup-text" }
const _hoisted_4 = { key: 2 }
const _hoisted_5 = { class: "mt-2" }
const _hoisted_6 = {
  key: 3,
  class: "verified"
}
const _hoisted_7 = { class: "verified__icon" }
const _hoisted_8 = { class: "verified__subtitle" }
const _hoisted_9 = { class: "mt-2" }

import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useAppStore } from "@/shared/stores/app";
import AppAuthBrand from "@/modules/auth/components/AppAuthBrand.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppOTPField from "@/shared/components/ui/AppOTPField.vue";
import {
  signup,
  passwordVerifyOTP,
  saveAuthToken,
  saveActiveContext,
} from "@/modules/auth/services/auth.api";
import { setContexts, setCustomer } from "@/modules/auth/services/auth.storage";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import fallbackLogo from "@/assets/s-logo.png";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'SignupPage',
  setup(__props) {

const step = ref<1 | 2 | 3>(1);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const fullName = ref("");
const businessName = ref("");
const businessEmail = ref("");
const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const otp = ref("");
const router = useRouter();
const nameError = ref("");
const businessNameError = ref("");
const businessEmailError = ref("");
const emailError = ref("");
const passwordError = ref("");
const confirmPasswordError = ref("");
const otpError = ref("");
const isSubmitting = ref(false);
const isVerifyingOtp = ref(false);
const appStore = useAppStore();
const snackbar = useSnackbarStore();
const companyName = computed(() =>
  (appStore.company?.name || appStore.appName || "GlobalApp").toString().trim()
);
const logo = computed(() => appStore.company?.logoUrl || fallbackLogo);
const subtitle = computed(() => {
  if (step.value === 1) return "Create your account.";
  if (step.value === 2) return `Enter the code sent to ${email.value || "your email"}.`;
  return "Your email has been verified.";
});
const brandData = computed(() => ({
  logo: logo.value,
  title: companyName.value,
  subtitle: subtitle.value,
}));

function validateInputs() {
  nameError.value = "";
  businessNameError.value = "";
  businessEmailError.value = "";
  emailError.value = "";
  passwordError.value = "";
  confirmPasswordError.value = "";

  if (!fullName.value.trim()) {
    nameError.value = "Full name is required";
  }

  if (!email.value.trim()) {
    emailError.value = "Email is required";
  } else if (!/.+@.+\..+/.test(email.value)) {
    emailError.value = "Enter a valid email address";
  }

  if (!password.value) {
    passwordError.value = "Password is required";
  } else if (password.value.length < 6) {
    passwordError.value = "Minimum 6 characters";
  }

  if (!confirmPassword.value) {
    confirmPasswordError.value = "Confirm your password";
  } else if (confirmPassword.value !== password.value) {
    confirmPasswordError.value = "Passwords do not match";
  }

  return (
    !nameError.value &&
    !emailError.value &&
    !passwordError.value &&
    !confirmPasswordError.value
  );
}

async function handleSubmit() {
  if (!validateInputs()) return;
  try {
    isSubmitting.value = true;
    const response = await signup({
      full_name: fullName.value,
      email: email.value,
      password: password.value,
      password_confirmation: confirmPassword.value,
    });

    // Normalize common response shapes: server may wrap payload under `data` or return fields at top-level.
    const data = response?.data ?? response;

    // Save token (support `token` or `access_token` at either level)
    const token = data?.token || data?.access_token || response?.token || response?.access_token;
    if (token) saveAuthToken(token);

    // Save active context (support multiple nesting shapes)
    const activeContext = data?.active_context || response?.active_context || data?.data?.active_context;
    if (activeContext) saveActiveContext(activeContext);

    // Persist contexts list if provided
    if (Array.isArray(data?.contexts)) {
      try {
        setContexts(data.contexts);
      } catch (_e) {
        // ignore
      }
    }

    // Persist client/customer info when available
    const client = data?.client || data?.customer || data;
    if (client && (client.id || client.email || client.first_name || client.name)) {
      const customerForStorage = {
        id: client?.id ?? client?.user_id ?? 0,
        first_name: client?.first_name ?? client?.fname ?? client?.name ?? "",
        last_name: client?.last_name ?? client?.lname ?? "",
        email: client?.email ?? "",
        phone: client?.phone ?? "",
        avatar: client?.avatar ?? "",
        first_login: typeof client?.first_login === "boolean" ? client.first_login : undefined,
      };
      try {
        setCustomer(customerForStorage);
      } catch (_e) {
        // ignore
      }
    }

    password.value = "";
    confirmPassword.value = "";
    passwordError.value = "";
    confirmPasswordError.value = "";
    otp.value = "";
    otpError.value = "";
    step.value = 2;
    snackbar.success("OTP sent to your email.");
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const message =
      error?.response?.data?.message || error?.response?.data?.error || "Signup failed";

    if (errors) {
      if (errors.full_name?.length) nameError.value = errors.full_name[0];
      if (errors.name?.length) nameError.value = errors.name[0];
      if (errors.email?.length) emailError.value = errors.email[0];
      if (errors.password?.length) passwordError.value = errors.password[0];
      if (errors.password_confirmation?.length)
        confirmPasswordError.value = errors.password_confirmation[0];
    }

    if (
      !nameError.value &&
      !emailError.value &&
      !passwordError.value &&
      !confirmPasswordError.value
    ) {
      passwordError.value = message;
    }
    console.error("Signup failed", error);
  } finally {
    isSubmitting.value = false;
  }
}

function validateOtp(length = 6) {
  otpError.value = "";
  const raw = otp.value?.toString().trim() || "";

  if (!raw) {
    otpError.value = "OTP is required";
    return false;
  }

  if (!/^\d+$/.test(raw)) {
    otpError.value = "OTP must be numbers only";
    return false;
  }

  if (raw.length !== length) {
    otpError.value = "Enter a valid OTP";
    return false;
  }

  return true;
}

async function handleVerifyOtp(value?: unknown) {
  if (isVerifyingOtp.value) return;
  isVerifyingOtp.value = true;
  try {
    // `@finish` emits the OTP string, but `@click="handleVerifyOtp"` passes a MouseEvent.
    if (typeof value === "string") otp.value = value;

    emailError.value = "";
    if (!email.value.trim()) {
      emailError.value = "Email is required";
      return;
    }
    if (!/.+@.+\..+/.test(email.value)) {
      emailError.value = "Enter a valid email address";
      return;
    }

    if (!validateOtp(6)) return;

    const response = await passwordVerifyOTP({
      email: email.value,
      otp: otp.value,
    });

    snackbar.success(response?.message || "Verified");
    step.value = 3;
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to verify OTP";

    if (errors) {
      if (errors.email?.length) emailError.value = errors.email[0];
      if (errors.otp?.length) otpError.value = errors.otp[0];
    }

    if (!otpError.value) {
      otpError.value = message;
    }
  } finally {
    isVerifyingOtp.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (step.value < 3)
        ? (_openBlock(), _createBlock(AppAuthBrand, {
            key: 0,
            data: brandData.value,
            logoAlt: companyName.value,
            "subtitle-size": "16px",
            "title-color": "#151a24",
            "subtitle-color": "#64748b",
            gap: "10px"
          }, null, 8, ["data", "logoAlt"]))
        : _createCommentVNode("", true),
      (step.value === 1)
        ? (_openBlock(), _createBlock(_component_v_form, {
            key: 1,
            class: "signup-form"
          }, {
            default: _withCtx(() => [
              _createVNode(AppTextField, {
                modelValue: fullName.value,
                "onUpdate:modelValue": [
                  _cache[0] || (_cache[0] = ($event: any) => ((fullName).value = $event)),
                  _cache[1] || (_cache[1] = ($event: any) => (nameError.value = ''))
                ],
                type: "text",
                autocomplete: "name",
                "prepend-inner-icon": "mdi-account-outline",
                placeholder: "Full name",
                class: "auth-input",
                "error-messages": nameError.value
              }, null, 8, ["modelValue", "error-messages"]),
              _createVNode(AppTextField, {
                modelValue: email.value,
                "onUpdate:modelValue": [
                  _cache[2] || (_cache[2] = ($event: any) => ((email).value = $event)),
                  _cache[3] || (_cache[3] = ($event: any) => (emailError.value = ''))
                ],
                type: "email",
                autocomplete: "email",
                "prepend-inner-icon": "mdi-email-outline",
                placeholder: "Email address",
                class: "auth-input text-lowercase",
                "error-messages": emailError.value
              }, null, 8, ["modelValue", "error-messages"]),
              _createVNode(AppTextField, {
                modelValue: password.value,
                "onUpdate:modelValue": [
                  _cache[4] || (_cache[4] = ($event: any) => ((password).value = $event)),
                  _cache[5] || (_cache[5] = ($event: any) => (passwordError.value = ''))
                ],
                type: showPassword.value ? 'text' : 'password',
                autocomplete: "new-password",
                "prepend-inner-icon": "mdi-lock-outline",
                "append-inner-icon": showPassword.value ? 'mdi-eye-off-outline' : 'mdi-eye-outline',
                placeholder: "Password",
                class: "auth-input",
                "error-messages": passwordError.value,
                "onClick:appendInner": _cache[6] || (_cache[6] = ($event: any) => (showPassword.value = !showPassword.value))
              }, null, 8, ["modelValue", "type", "append-inner-icon", "error-messages"]),
              _createVNode(AppTextField, {
                modelValue: confirmPassword.value,
                "onUpdate:modelValue": [
                  _cache[7] || (_cache[7] = ($event: any) => ((confirmPassword).value = $event)),
                  _cache[8] || (_cache[8] = ($event: any) => (confirmPasswordError.value = ''))
                ],
                type: showConfirmPassword.value ? 'text' : 'password',
                autocomplete: "new-password",
                "prepend-inner-icon": "mdi-lock-check-outline",
                "append-inner-icon": 
            showConfirmPassword.value ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
          ,
                placeholder: "Confirm password",
                class: "auth-input",
                "error-messages": confirmPasswordError.value,
                "onClick:appendInner": _cache[9] || (_cache[9] = ($event: any) => (showConfirmPassword.value = !showConfirmPassword.value))
              }, null, 8, ["modelValue", "type", "append-inner-icon", "error-messages"]),
              _createVNode(AppButtonFlat, {
                onClick: handleSubmit,
                loading: isSubmitting.value,
                disabled: isSubmitting.value
              }, {
                default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                  _createTextVNode(" Create account ", -1)
                ]))]),
                _: 1
              }, 8, ["loading", "disabled"]),
              _createElementVNode("p", _hoisted_3, [
                _cache[15] || (_cache[15] = _createTextVNode(" Already have an account? ", -1)),
                _createElementVNode("button", {
                  type: "button",
                  class: "signup-link",
                  onClick: _cache[10] || (_cache[10] = ($event: any) => (_unref(router).push({ name: 'login.page' })))
                }, " Sign In ")
              ])
            ]),
            _: 1
          }))
        : (step.value === 2)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_component_v_form, { class: "signup-form" }, {
                default: _withCtx(() => [
                  _createVNode(AppOTPField, {
                    modelValue: otp.value,
                    "onUpdate:modelValue": [
                      _cache[11] || (_cache[11] = ($event: any) => ((otp).value = $event)),
                      _cache[12] || (_cache[12] = ($event: any) => (otpError.value = ''))
                    ],
                    length: 6,
                    type: "number",
                    "error-message": otpError.value,
                    onFinish: handleVerifyOtp
                  }, null, 8, ["modelValue", "error-message"])
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_5, [
                _createVNode(AppButtonFlat, {
                  block: "",
                  onClick: handleVerifyOtp,
                  loading: isVerifyingOtp.value,
                  disabled: isVerifyingOtp.value
                }, {
                  default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                    _createTextVNode(" Verify OTP ", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading", "disabled"])
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createVNode(_component_v_icon, { size: "34" }, {
                  default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                    _createTextVNode("mdi-check-circle", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _cache[19] || (_cache[19] = _createElementVNode("h3", { class: "verified__title" }, "Email verified", -1)),
              _createElementVNode("p", _hoisted_8, _toDisplayString(email.value) + " has been verified successfully.", 1),
              _createElementVNode("div", _hoisted_9, [
                _createVNode(AppButtonFlat, {
                  block: "",
                  onClick: _cache[13] || (_cache[13] = ($event: any) => (_unref(router).push({ name: 'vendor.dashboard' })))
                }, {
                  default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                    _createTextVNode(" Go to Dashboard ", -1)
                  ]))]),
                  _: 1
                })
              ])
            ]))
    ])
  ]))
}
}

})